// Stockage local de l'état des lieux (F-103) : brouillon et photos conservés sur l'appareil quand le réseau manque.
// IndexedDB peut être indisponible (navigation privée, stockage bloqué) : chaque accès est protégé et l'écran le signale.
import { useEffect, useState } from "react";
import type { InspectionContent } from "./shared";

const DB_NAME = "bailivo-entree";
const DB_VERSION = 1;

export type LocalDraft = {
  id: string;
  orgId: string;
  leaseId: string;
  content: InspectionContent;
  /** Révision du serveur sur laquelle la saisie locale est fondée. */
  baseRevision: number;
  /** Modifications locales non encore envoyées. */
  dirty: boolean;
  savedAt: string;
};

export type QueuedPhoto = {
  clientKey: string;
  inspectionId: string;
  orgId: string;
  roomKey: string;
  itemKey: string | null;
  caption: string;
  filename: string;
  mime: string;
  blob: Blob;
  createdAt: string;
};

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains("drafts")) db.createObjectStore("drafts", { keyPath: "id" });
        if (!db.objectStoreNames.contains("photos")) {
          const s = db.createObjectStore("photos", { keyPath: "clientKey" });
          s.createIndex("inspection", "inspectionId");
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function run<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | null): Promise<T | null> {
  return openDb().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) return resolve(null);
        try {
          const tx = db.transaction(store, mode);
          const req = fn(tx.objectStore(store));
          if (!req) {
            tx.oncomplete = () => resolve(null);
            tx.onerror = () => resolve(null);
            return;
          }
          req.onsuccess = () => resolve(req.result ?? null);
          req.onerror = () => resolve(null);
        } catch {
          resolve(null);
        }
      }),
  );
}

export async function localAvailable(): Promise<boolean> {
  return (await openDb()) !== null;
}

export function getDraft(id: string): Promise<LocalDraft | null> {
  return run<LocalDraft>("drafts", "readonly", (s) => s.get(id) as IDBRequest<LocalDraft>);
}

export async function putDraft(d: LocalDraft): Promise<boolean> {
  const r = await run("drafts", "readwrite", (s) => s.put(d));
  return r !== null;
}

export async function deleteDraft(id: string): Promise<void> {
  await run("drafts", "readwrite", (s) => s.delete(id));
}

export async function listPhotos(inspectionId: string): Promise<QueuedPhoto[]> {
  const r = await run<QueuedPhoto[]>("photos", "readonly", (s) => s.index("inspection").getAll(inspectionId) as IDBRequest<QueuedPhoto[]>);
  return r ?? [];
}

export async function putPhoto(p: QueuedPhoto): Promise<boolean> {
  const r = await run("photos", "readwrite", (s) => s.put(p));
  return r !== null;
}

export async function deletePhoto(clientKey: string): Promise<void> {
  await run("photos", "readwrite", (s) => s.delete(clientKey));
}

/** Efface tout ce que cet appareil conserve pour un état des lieux (brouillon et photos en attente). */
export async function clearInspection(id: string): Promise<void> {
  await deleteDraft(id);
  for (const p of await listPhotos(id)) await deletePhoto(p.clientKey);
}

/** Nombre d'états des lieux et de photos conservés sur l'appareil. */
export async function localSummary(): Promise<{ drafts: number; photos: number }> {
  const drafts = (await run<LocalDraft[]>("drafts", "readonly", (s) => s.getAll() as IDBRequest<LocalDraft[]>)) ?? [];
  const photos = (await run<QueuedPhoto[]>("photos", "readonly", (s) => s.getAll() as IDBRequest<QueuedPhoto[]>)) ?? [];
  return { drafts: drafts.length, photos: photos.length };
}

export async function clearAll(): Promise<void> {
  await run("drafts", "readwrite", (s) => s.clear());
  await run("photos", "readwrite", (s) => s.clear());
}

/** État du réseau vu par le navigateur (indicatif : une requête peut échouer même « en ligne »). */
export function useOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}
